/**
 * システム設定: BB ID・内線番号の生成ルール(2026-09-09 ②・⑧指示)。
 *   BB ID = 出勤形態×報酬区分×架電内容 の形態コード＋連番 / 内線番号 = その連番の数字
 *   給湯器: 品川 SN＋1000〜 / 在宅時給 SR＋2000〜 / 在宅歩合 RS＋4000〜 / タイミー STM＋001〜(時給のみ)
 *   不用品: 給湯器のコード＋W(仮置き) / 管理: SNM＋01〜(仮置き)
 * ゆくゆくは API 連携で システム → BlueBean に自動反映する(Phase 5)。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useCurrentUser } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import {
  BB_PREFIX_KEYS,
  BB_STYLE_KEYS,
  BB_STYLE_LABELS,
  BUSINESS_LABELS,
  DEFAULT_BB_NUMBERING,
  bbPrefixKey,
  type BbNumberingSettings,
  type BbPrefixKey,
  type BusinessType,
} from '@core/lib/types';
import { fetchBbNumbering, saveBbNumbering } from './bbNumbering';

type RuleText = { prefix: string; start: string; digits: string };

function toText(s: BbNumberingSettings): Record<BbPrefixKey, RuleText> {
  return Object.fromEntries(
    BB_PREFIX_KEYS.map((k) => [k, { prefix: s.rules[k].prefix, start: String(s.rules[k].start), digits: String(s.rules[k].digits) }]),
  ) as Record<BbPrefixKey, RuleText>;
}

const cellInput = { padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit' } as const;
const BUSINESSES = Object.keys(BUSINESS_LABELS) as BusinessType[];

export default function SettingsSystemPage() {
  const user = useCurrentUser();
  const toast = useToast();
  const [rules, setRules] = useState<Record<BbPrefixKey, RuleText>>(toText(DEFAULT_BB_NUMBERING));
  /** 保存済みの値(未保存の判定に使う) */
  const [saved, setSaved] = useState<Record<BbPrefixKey, RuleText>>(toText(DEFAULT_BB_NUMBERING));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(rules) !== JSON.stringify(saved);
  const guard = useUnsavedGuard(dirty);

  useEffect(() => {
    void fetchBbNumbering()
      .then((s) => {
        setRules(toText(s));
        setSaved(toText(s));
      })
      .catch(() => toast('採番ルールの読み込みに失敗しました(既定値を表示しています)'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setRule(key: BbPrefixKey, patch: Partial<RuleText>) {
    setRules((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const settings: BbNumberingSettings = {
        rules: Object.fromEntries(
          BB_PREFIX_KEYS.map((k) => [k, { prefix: rules[k].prefix.trim(), start: Number(rules[k].start), digits: Number(rules[k].digits) }]),
        ) as BbNumberingSettings['rules'],
      };
      await saveBbNumbering(settings, user.id);
      setSaved(rules);
      toast('保存しました');
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const sample = (key: BbPrefixKey) => {
    const n = String(Number(rules[key].start) || 0).padStart(Number(rules[key].digits) || 1, '0');
    return { id: `${rules[key].prefix}${n}`, ext: n };
  };

  const ruleCells = (k: BbPrefixKey) => (
    <>
      <td>
        <input value={rules[k].prefix} onChange={(e) => setRule(k, { prefix: e.target.value })} style={{ ...cellInput, width: 80 }} />
      </td>
      <td>
        <input inputMode="numeric" value={rules[k].start} onChange={(e) => setRule(k, { start: e.target.value })} style={{ ...cellInput, width: 90 }} />
      </td>
      <td>
        <input inputMode="numeric" value={rules[k].digits} onChange={(e) => setRule(k, { digits: e.target.value })} style={{ ...cellInput, width: 60 }} />
      </td>
      <td>
        <span className="badge b-plan">{sample(k).id}</span> <span className="hint">内線 {sample(k).ext}</span>
      </td>
    </>
  );

  return (
    <div className="screen">
      {guard.modal}
      <div className="card">
        <h3>BB ID・内線番号の生成ルール</h3>
        <p className="hint" style={{ marginBottom: 14 }}>
          BB ID＝形態コード＋連番、内線番号＝連番の数字。番号は「開始値」と「登録済みの最大＋1」の大きい方から続きます(タイミーは時給のみ)。
        </p>
        {loading ? (
          <div className="empty">読み込み中…</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* 出勤形態の縦結合(rowSpan)があるため縦積みにせず、スマホでは表の中だけ横スクロール */}
            <div className="table-scroll">
            <table className="no-stack" style={{ maxWidth: 860 }}>
              <thead>
                <tr>
                  <th>出勤形態×報酬区分</th>
                  <th>架電内容</th>
                  <th>形態コード</th>
                  <th>開始値</th>
                  <th>桁数</th>
                  <th>見本(BB ID / 内線)</th>
                </tr>
              </thead>
              <tbody>
                {BB_STYLE_KEYS.flatMap((s) =>
                  BUSINESSES.map((b, i) => {
                    const k = bbPrefixKey(s, b);
                    return (
                      <tr key={k}>
                        {i === 0 && (
                          <td rowSpan={BUSINESSES.length} style={{ fontWeight: 600, verticalAlign: 'top' }}>
                            {BB_STYLE_LABELS[s]}
                            {s === 'kanri' && <span className="hint"> (仮)</span>}
                          </td>
                        )}
                        <td>
                          <span className={`badge ${b === 'kyutoki' ? 'b-plan' : 'b-pend'}`}>{BUSINESS_LABELS[b]}</span>
                        </td>
                        {ruleCells(k)}
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table>
            </div>
            <div className="modal-actions">
              <button className="btn-green" type="submit" disabled={busy}>
                保存する
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="card">
        <h3>確認事項</h3>
        <ul style={{ paddingLeft: 20, fontSize: 13, color: 'var(--muted)', lineHeight: 1.9 }}>
          <li>不用品(ホワイト)の形態コードは指示に無いため、給湯器のコード＋W(SNW／SRW／RSW／STMW)を仮置きしています。現行のBlueBeanでは不用品用アカウントも SR2xxx(表示名に「W」)のため、既存IDはそのまま、今後の自動生成から新コードになります</li>
          <li>出勤形態「管理」の形態コードは指示に無いため、現行シートの SNM＋01〜 を仮置きしています</li>
          <li>BlueBean側へのID・パスワード登録は当面手作業。API連携(システム→BlueBean 自動反映)はソフツー回答後に実装</li>
        </ul>
      </div>
    </div>
  );
}
